import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Material-UI
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';

import { Icons, Theme } from '../../styles';

const styles = {
  /* Styles applied to the characterLimit element. */
  characterLimit: {
    flex: 1,
    textAlign: 'right',
  }
};

const useStyles = makeStyles(() => ({
  menuPaper: {
    maxHeight: 256 // 6 Items
  },
  list: {
    backgroundColor: Theme.colors.white,
    backfaceVisibility: 'hidden'
  }
}));

const fancyCount = (str) => {
  str = str ? str.toString(16) : '';
  const joiner = '\u{200D}';
  const split = str.split(joiner);
  let count = 0;

  for (const s of split) {
    // Removing the variation selectors
    const num = Array.from(s.split(/[\ufe00-\ufe0f]/).join('')).length;
    count += num;
  }

  // Assuming the joiners are used appropriately
  return count / split.length;
};

/**
 * For advanced cases.
 * - Using the upper case props for passing values directly to the components
 */
const MuiTextField = (props) => {
  const {
    // autoComplete,
    autoFocus,
    characterLimit,
    children,
    defaultValue,
    fullWidth,
    helperText,
    id,
    inputProps,
    inputRef,
    label,
    multiline,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    rows,
    rowsMax,
    select,
    type,
    value,
    InputProps,
    InputLabelProps,
    FormHelperTextProps,
    SelectProps,
    ...restProps
  } = props;

  const classes = useStyles();

  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  const helperTextCounterId = helperText && id ? `${id}-helper-text-counter` : undefined;

  const InputComponent = Input;
  const InputElement = (
    <InputComponent
      aria-describedby={helperTextId}
      autoFocus={autoFocus}
      defaultValue={defaultValue}
      fullWidth={fullWidth}
      id={id}
      inputProps={inputProps}
      inputRef={inputRef}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      placeholder={placeholder}
      multiline={multiline}
      name={name}
      rows={rows}
      rowsMax={rowsMax}
      type={type}
      value={value}
      {...InputProps}
    />
  );

  return (
    <FormControl {...restProps}>
      {/* Label */}
      {label && (
        <InputLabel
          htmlFor={id}
          {...InputLabelProps}
        >
          {label}
        </InputLabel>
      )}
      {/* Input */}
      {select ? (
        <Select
          aria-describedby={helperTextId}
          input={InputElement}
          IconComponent={Icons.ArrowDown}
          value={value}
          MenuProps={{ classes: { paper: classes.menuPaper, list: classes.list } }}
          onClose={() => setTimeout(() => { document.activeElement.blur(); }, 0)}
          {...SelectProps}
        >
          {children}
        </Select>
      ) : (
        InputElement
      )}
      {/* Helper Text */}
      <div style={{ display: 'flex' }}>
        {helperText && (
          <FormHelperText
            id={helperTextId}
            {...FormHelperTextProps}
          >
            {helperText}
          </FormHelperText>
        )}
        {characterLimit && (
          <FormHelperText
            style={styles.characterLimit}
            id={helperTextCounterId}
            {...FormHelperTextProps}
          >
            {`${fancyCount(value)}/${characterLimit}`}
          </FormHelperText>
        )}
      </div>
    </FormControl>
  );
};

MuiTextField.propTypes = {
  // The name can be confusing, as it's more like an autofill.
  autoComplete: PropTypes.string,
  // If 'true', the 'input' element will be focused during the first mount.
  autoFocus: PropTypes.bool,
  // Character limit.
  characterLimit: PropTypes.number,
  // * @ignore.
  children: PropTypes.node,
  // The default value of the 'input' element.
  defaultValue: PropTypes.any,
  // If 'true', the input will take up the full width of its container.
  fullWidth: PropTypes.bool,
  // The helper text content.
  helperText: PropTypes.node,
  // The id of the 'input' element.
  id: PropTypes.string,
  // [Attributes] applied to the 'input' element.
  inputProps: PropTypes.object,
  // Pass a ref to the 'input' element.
  inputRef: PropTypes.any,
  // The label content.
  label: PropTypes.node,
  // If 'true', a textarea element will be rendered instead of an input.
  multiline: PropTypes.bool,
  // Name attribute of the 'input' element.
  name: PropTypes.string,
  // @ignore
  onBlur: PropTypes.func,
  // Callback fired when the value is changed.
  onChange: PropTypes.func,
  // @ignore
  onFocus: PropTypes.func,
  // The short hint displayed in the input before the user enters a value.
  placeholder: PropTypes.string,
  // Number of rows to display when multiline option is set to true.
  rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Maximum number of rows to display when multiline option is set to true.
  rowsMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Type of the 'input' element.
  type: PropTypes.string,
  // The value of the 'input' element, required for a controlled component.
  value: PropTypes.any,
  // Render a ['Select'] element while passing the Input element to 'Select' as 'input' parameter.
  // If this option is set you must pass the options of the select as children.
  select: PropTypes.bool,
  // Properties applied to the [InputLabel] element.
  InputLabelProps: PropTypes.object,
  // Properties applied to the [Input] element.
  InputProps: PropTypes.object,
  // Properties applied to the [FormHelperText] element.
  FormHelperTextProps: PropTypes.object,
  // Properties applied to the [Select] element.
  SelectProps: PropTypes.object,
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

// Standard TextField
export const StyledTextField = styled(MuiTextField)`
  &.MuiFormControl-root {
    width: 100%;
    min-height: 68px;
  }

  .MuiInputLabel-root {
    /* font-family: 'Bryant Pro Regular'; */
    font-size: ${(props) => props.theme.typography.bodyText};
    line-height: 1;
    letter-spacing: 0.0175em;
    color: ${(props) => props.theme.colors.greyDarkAlpha};
    display: flex;
    align-items: center;
    //height: 18px;
  }

  .MuiInputBase-root {
    /* font-family: 'Bryant Pro Regular'; */
    font-size: ${(props) => props.theme.typography.bodyText};
    line-height: normal;
    letter-spacing: 0.0175em;
    color: ${(props) => props.theme.colors.greyDark};
    min-height: 30px;
  }

  .MuiFormHelperText-root {
    /* font-family: 'Bryant Pro Regular'; */
    font-size: ${(props) => props.theme.typography.smallText};
    line-height: 1.5;
    letter-spacing: 0.0175em;
    margin-top: 2px;
    color: ${(props) => props.theme.colors.greyDark};
  }

  .MuiInputBase-root.Mui-focused {
    color: ${(props) => props.theme.colors.blueMedium};
  }

  .MuiFormLabel-root.Mui-focused {
    color: ${(props) => props.theme.colors.blueMedium};
  }

  .MuiInputBase-root.Mui-error,
  .MuiFormLabel-root.Mui-error,
  .MuiFormLabel-asterisk.Mui-error,
  .MuiFormHelperText-root.Mui-error {
    color: ${(props) => props.theme.colors.red};
  }

  .MuiInputBase-multiline {
    padding: 0;
  }

  .MuiInputBase-inputMultiline {
    max-height: 85px !important;
    overflow-y: auto !important;
    line-height: 1.3;
  }

  .MuiInputBase-input {
    color: ${(props) => props.theme.colors.greyDark};
    padding: 0px 0px;
  }

  .MuiInputBase-input.Mui-disabled {
    color: ${(props) => props.theme.colors.greyDarkAlpha}
  }

  .MuiFormLabel-filled {
    transform: translate(0, 1.5px) scale(0.90);
  }

  .MuiFormLabel-root.Mui-focused {
    transform: translate(0, 1.5px) scale(0.90);
  }

  .MuiInput-underline:before {
    border-bottom: 1px solid ${(props) => props.theme.colors.greyMedium};
  }

  .MuiInput-underline:after {
    border-bottom: 2px solid ${(props) => props.theme.colors.blueMedium};
  }

  .MuiInput-underline:hover:not(.Mui-disable):before {
    border-bottom: 2px solid ${(props) => props.theme.colors.greyDark};
  }

  .MuiInput-underline.Mui-error:after {
    border-bottom: 1px solid ${(props) => props.theme.colors.red};
  }

  .MuiInput-underline.Mui-error:hover:not(.Mui-disable):before {
    border-bottom: 2px solid ${(props) => props.theme.colors.red};
  }

  .MuiInput-underline.Mui-disabled:before {
    border-bottom: 1px dotted ${(props) => props.theme.colors.greyMedium} !important;
  }

  .MuiFormLabel-root.Mui-disabled,
  .MuiInputBase-root.Mui-disabled {
    color: ${(props) => props.theme.colors.greyDarkAlpha}
  }

  .MuiSelect-icon {
    color: ${(props) => props.theme.colors.greyDark};
    font-size: 20px;
  }

  .MuiSelect-select.MuiSelect-select {
    padding-right: 24px !important;
  }

  .MuiPaper-rounded {
    border-radius: 6px;
  }  

  .MuiSvgIcon-root {
    font-size: 20px;
  } 
`;

// Standard TextField Icon
export const StyledTextFieldIcon = styled(StyledTextField)`
  .MuiInputLabel-root {
    left: 28px;
    transform: translate(0,24px) scale(1);
  }

  .MuiFormLabel-filled {
    transform: translate(0, 1.5px) scale(0.90);
  }

  .MuiInputBase-input {
    margin-left: 8px;
  }

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

// Text Field (Option Item)
export const StyledSelectOption = styled(MenuItem)`
  &.MuiMenuItem-root {
    /* font-family: 'Bryant Pro Regular'; */
    font-size: ${(props) => props.theme.typography.bodyText};
    color: ${(props) => props.theme.colors.greyDark};
    padding: 0px 16px;
    min-height: 40px;
    letter-spacing: 0.0175em;
    line-height: normal;
    white-space: normal;
  }
  
  &.MuiListItem-root.Mui-selected {
    color: ${(props) => props.theme.colors.blueMedium};
  }
`;

// Input Number Slider
export const StyledInputSlider = styled(StyledTextField)`
  &.MuiFormControl-root {
    width: 100%;
    min-height: auto;
    padding-left: 16px;
  }

  .MuiInputBase-input {
    height: 30px;
    padding-left: 10px;
    text-align: center;
    border-radius: 6px;
    border: 1px solid ${(props) => props.theme.colors.greyMedium};
  }

  .MuiInput-underline:before {
    border: none;
  }
`;